import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { WorkspaceRail, WORKSPACE_STEPS } from "@/components/workspace/WorkspaceRail";
import { ClarifyStage } from "@/components/workspace/ClarifyStage";
import { PrioritizeStage } from "@/components/workspace/PrioritizeStage";
import { EvaluatingState, ENGINE_STAGES } from "@/components/workspace/EvaluatingState";
import { RecommendationStage } from "@/components/workspace/RecommendationStage";

export default function Workspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [answers, setAnswers] = useState({});
  const [weights, setWeights] = useState({});
  const [stageIdx, setStageIdx] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/decisions/${id}`);
      setD(r.data);
      const w = {};
      (r.data.criteria || []).forEach((c) => (w[c.id] = r.data.weights?.[c.id] ?? 3));
      setWeights(w);
    } catch {
      toast.error(t("workspace.notFound"));
      nav("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, nav]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (busy !== "evaluate") return undefined;
    setStageIdx(0);
    const t = setInterval(
      () => setStageIdx((i) => (i + 1) % ENGINE_STAGES.length),
      4000
    );
    return () => clearInterval(t);
  }, [busy]);

  if (!d) {
    return (
      <Layout>
        <div className="mx-auto max-w-6xl px-6 py-12">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="mt-6 h-64 w-full" />
        </div>
      </Layout>
    );
  }

  const submitClarify = async () => {
    setBusy("clarify");
    try {
      const r = await api.post(`/decisions/${id}/clarify`, { answers });
      setD(r.data);
    } catch (e) {
      toast.error(t("workspace.clarifyError"));
    } finally {
      setBusy("");
    }
  };

  const submitPriorities = async () => {
    setBusy("evaluate");
    try {
      await api.post(`/decisions/${id}/priorities`, { weights });
      const r = await api.post(`/decisions/${id}/evaluate`);
      setD(r.data);
      toast.success("Recommendation ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Evaluation failed. Please retry.");
    } finally {
      setBusy("");
    }
  };

  const refine = async (w) => {
    setBusy("refine");
    try {
      const r = await api.post(`/decisions/${id}/refine`, { weights: w });
      setD(r.data);
      setWeights(w);
      toast.success(t("workspace.prioritiesApplied"));
    } catch {
      toast.error(t("workspace.refineFailed"));
    } finally {
      setBusy("");
    }
  };

  const runChallenge = async () => {
    setBusy("challenge");
    try {
      const r = await api.post(`/decisions/${id}/challenge`);
      setD((p) => ({ ...p, challenge: r.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("workspace.challengeFailed"));
    } finally {
      setBusy("");
    }
  };

  const runSecondOpinion = async () => {
    setBusy("second");
    try {
      const r = await api.post(`/decisions/${id}/second-opinion`);
      setD((p) => ({
        ...p,
        second_opinion: r.data.second_opinion,
        recommendation: r.data.recommendation,
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("workspace.secondOpinionFailed"));
    } finally {
      setBusy("");
    }
  };

  const currentStepKey =
    d.status === "recommended"
      ? "recommended"
      : d.status === "clarify"
      ? "clarify"
      : "prioritize";
  const stepIndex = WORKSPACE_STEPS.findIndex((s) => s.key === currentStepKey);

  return (
    <Layout>
      <div className="grid lg:grid-cols-[340px_1fr]">
        <WorkspaceRail d={d} onBack={() => nav("/")} stepIndex={stepIndex} />

        <section className="p-6 sm:p-10">
          <div className="mx-auto max-w-3xl space-y-8">
            {currentStepKey === "clarify" && (
              <ClarifyStage
                questions={d.clarifying_questions}
                answers={answers}
                setAnswers={setAnswers}
                onSubmit={submitClarify}
                busy={busy}
              />
            )}

            {currentStepKey === "prioritize" && busy !== "evaluate" && (
              <PrioritizeStage
                criteria={d.criteria}
                weights={weights}
                setWeights={setWeights}
                onSubmit={submitPriorities}
              />
            )}

            {busy === "evaluate" && <EvaluatingState stageIdx={stageIdx} />}

            {currentStepKey === "recommended" && busy !== "evaluate" && (
              <RecommendationStage
                d={d}
                id={id}
                onRefine={refine}
                onChallenge={runChallenge}
                onSecondOpinion={runSecondOpinion}
                busy={busy}
              />
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
